import { useState } from "react";
import Button from "@/components/Button";
import Input from "@/components/Input";
import RadioGroup from "@/components/RadioGroup";
import { useQuiz } from "./useQuiz";
import type { Question, Answer } from "@/types/extended-types";

interface QuizProps {
  questions: Question[];
  onComplete: (answers: Answer[], timeSpentSeconds: number) => void;
  onCancel: () => void;
}

const Quiz = ({ questions, onComplete, onCancel }: QuizProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuizComplete = (answers: Answer[], timeSpentSeconds: number) => {
    if (isSubmitting) return; // Prevent multiple submissions
    setIsSubmitting(true);
    onComplete(answers, timeSpentSeconds);
  };

  const {
    currentQuestion,
    currentQuestionIndex,
    progress,
    isLastQuestion,
    getCurrentAnswer,
    setAnswer,
    goToNext,
    goToPrevious,
    canGoNext,
    canGoPrevious,
  } = useQuiz({ questions, onComplete: handleQuizComplete });
  const handleNext = async () => {
    if (isLastQuestion && isSubmitting) return; // Prevent multiple clicks
    goToNext();
  };

  if (!currentQuestion) {
    return (
      <div className="text-center py-8">
        <div className="text-error-500 text-6xl mb-4">❓</div>
        <h2 className="text-xl font-bold text-error-500 mb-2">
          No questions available
        </h2>
        <p className="text-neutral-500 mb-4">
          There are no quiz questions for this article.
        </p>
        <Button onClick={onCancel} variant="outline">
          Back to Article
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto bg-neutral-200 rounded-[15px] flex flex-col gap-y-7">
      <div className="w-full h-2 bg-neutral-200 rounded-t-[15px]">
        {progress > 0.1 && (
          <div
            className="h-full bg-action-300 rounded-t-[15px]"
            style={{ width: `${progress}%` }}
          ></div>
        )}
      </div>
      <div className="p-8 flex flex-col gap-y-[75px]">
        <div className="flex flex-col gap-y-7">
          <div className="flex flex-col gap-y-4">
            <p className="text-article-500 font-xs">
              Question {currentQuestionIndex + 1}
            </p>
            <p className="text-[24px] font-bold">{currentQuestion.question}</p>
          </div>
          {currentQuestion.type === "LO" ? (
            <Input
              type="text"
              onChange={(e) => setAnswer(e.target.value)}
              value={getCurrentAnswer()}
              onKeyPress={(e) => {
                if (e.key === "Enter" && canGoNext) {
                  handleNext();
                }
              }}
            />
          ) : (
            <div className="flex flex-col gap-y-4">
              <RadioGroup
                options={
                  currentQuestion.choices || currentQuestion.options || []
                }
                value={getCurrentAnswer()}
                onChange={setAnswer}
                name={`question-${currentQuestion.id}`}
              />
            </div>
          )}
        </div>
        <div className="w-full flex justify-between">
          <Button
            onClick={goToPrevious}
            disabled={!canGoPrevious}
            variant="outline"
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            variant="primary"
            disabled={!canGoNext || (isLastQuestion && isSubmitting)}
          >
            {isLastQuestion && isSubmitting
              ? "Submitting..."
              : isLastQuestion
                ? "Finish"
                : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
